
import { Component, Vue, Watch, Prop } from 'vue-property-decorator';
import { BCard } from 'bootstrap-vue';
import { ICalendarHalfDay } from '@/entity/shared/calendar-day-picker';
import { IPlanificationPhaseProfile } from '@/entity/planification/planification-phase-profile';
import { ScenarioOption, WeekDayModel } from './interface/period-selector';
import { dateDisabled } from './calendar-helper';
@Component({
    components: {
        BCard
    }
})
export default class PeriodSelector extends Vue {
    @Prop({ required: true }) selectedProfil!: IPlanificationPhaseProfile | null;

    inputStartDate: string = '';
    inputEndDate: string = '';

    selectionMode: any = {
        value: 1,
        text: 'Toutes les semaines'
    };

    firstSelectedWeekdayDate : Date | null = null;

    weekInterval: number = 1;
    everyNthWeek: number = 1;
    monthInterval: number = 1;

    hasConflict: boolean = false;
    conflictMessage: string = '';

    get selectedWeekdays() {
        return this.weekdays.filter(d => d.selected);
    }

    get isApplicable(): boolean {
        if (!this.inputStartDate || !this.inputEndDate) return false;
        return new Date(this.inputStartDate) <= new Date(this.inputEndDate) && this.validateWeekdays();
    }

    @Watch('inputStartDate')
    setInputEndDate(newVal) {
        if (this.inputStartDate && !this.inputEndDate) this.inputEndDate = newVal;
    }

    dateDisabled(_: any, date: Date) {
        return dateDisabled(_, date);
    }

    clampWeekInterval() {
        this.weekInterval = Math.min(Math.max(this.weekInterval, 1), 4);
    }

    clampMonthInterval() {
        this.monthInterval = Math.min(Math.max(this.monthInterval, 1), 12);
    }
    
    clampEveryNthWeek() {
        this.everyNthWeek = Math.min(Math.max(this.everyNthWeek, 1), 4);
    }

    selectedWorkDays: ICalendarHalfDay[] = [];

    selectionOptions: ScenarioOption[] = [
        { value: 1, text: 'Toutes les semaines' },
        { value: 2, text: 'Toutes les X semaines' },
        { value: 3, text: 'Toutes les Xᵉ semaines dans tous les Y mois' }
    ];

    weekdays: WeekDayModel[] = [
        { name: 'Lundi', code: 1, selected: false, morning: false, afternoon: false },
        { name: 'Mardi', code: 2, selected: false, morning: false, afternoon: false },
        { name: 'Mercredi', code: 3, selected: false, morning: false, afternoon: false },
        { name: 'Jeudi', code: 4, selected: false, morning: false, afternoon: false },
        { name: 'Vendredi', code: 5, selected: false, morning: false, afternoon: false }
    ];

    toggleDay(day: WeekDayModel) {
        day.selected = !day.selected;
        if (day.selected) {
            day.morning = true;
            day.afternoon = true;
        } else {
            day.morning = false;
            day.afternoon = false;
        }
    }

    setAfternoon (day: WeekDayModel) {
        if (day.afternoon) {
            day.selected = true;
        } else if (!day.afternoon && !day.morning) {
            day.selected = false;
        }
    }
  
    setMorning (day: WeekDayModel) {
        if (day.morning) {
            day.selected = true;
        } else if (!day.afternoon && !day.morning) {
            day.selected = false;
        }
    }

    private getFirstSelectedWeekdayDate(): Date | null {
        if (!this.inputStartDate) return null;

        const firstSelected = this.weekdays.find(d => d.selected);
        if (!firstSelected) return null;

        const start = new Date(this.inputStartDate);
        const currentDay = start.getDay();

        const diff = (firstSelected.code - currentDay + 7) % 7;

        const result = new Date(start);
        result.setDate(start.getDate() + diff);

        return result;
    }

    validateWeekdays(): boolean {
        return this.selectedWeekdays.length > 0 && this.selectedWeekdays.every(day => day.morning || day.afternoon);
    }

    private buildConflictMessage(
        repeated: ICalendarHalfDay[]
    ): string {
        const dayNames: Record<number, string> = {
            1: 'Lundi',
            2: 'Mardi',
            3: 'Mercredi',
            4: 'Jeudi',
            5: 'Vendredi'
        };

        const uniqueDays = new Set<string>();

        repeated.forEach(item => {
            const date = new Date(item.year, item.month, item.day);
            const jsDay = date.getDay();
            uniqueDays.add(dayNames[jsDay]);
        });

        const daysArray = Array.from(uniqueDays);

        const days = daysArray
            .map(day => `<strong>${day}s</strong>`)
            .join(', ');

        return `Les ${days} sont déjà planifiés sur cette période.`;
    }

    private compareWithExistingProfileWorkDays(): void {
        const repeated: ICalendarHalfDay[] = [];
        const nonRepeated: ICalendarHalfDay[] = [];

        const existingList = this.selectedProfil?.planificationPhaseProfileWorkDays;

        if (existingList) {
            const persistedDays = existingList.filter(item => item.id !== 0);

            const existingSet = new Set(
                persistedDays.map(item => {
                    const d = item.date;
                    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}_${item.dayPartTypeId}`;
                })
            );

            for (const item of this.selectedWorkDays) {
                const key = `${item.year}-${item.month}-${item.day}_${item.dayPartId}`;

                if (existingSet.has(key)) {
                    repeated.push(item);
                } else {
                    nonRepeated.push(item);
                }
            }

            if (repeated.length > 0) {
                this.hasConflict = true;
                this.conflictMessage = this.buildConflictMessage(repeated);
                return;
            }
            this.$emit('apply', nonRepeated);
        } else {
            this.$emit('apply', this.selectedWorkDays);
        }
    }

    applySelection(): void {
        this.selectedWorkDays = [];
        this.hasConflict = false;

        const start = new Date(this.inputStartDate);
        const end = new Date(this.inputEndDate ?? this.inputStartDate);

        this.firstSelectedWeekdayDate = this.getFirstSelectedWeekdayDate();

        if (this.selectedWeekdays.length === 0) return;

        // Mode 3 handled separately
        if (this.selectionMode === 3) {
            this.generateNthWeekInMonths(start, end);
            return;
        }

        const current = new Date(start);

        // eslint-disable-next-line no-unmodified-loop-condition
        while (current <= end) {
            const day = current.getDay();

            if (this.firstSelectedWeekdayDate && (current < this.firstSelectedWeekdayDate)) {
                current.setDate(current.getDate() + 1);
                continue;
            }

            // Skip weekend if needed
            if (day !== 0 && day !== 6) {
                if (this.selectionMode === 2) {
                    const anchor = this.firstSelectedWeekdayDate ?? start;

                    const anchorDate = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());
                    const currentDate = new Date(current.getFullYear(), current.getMonth(), current.getDate());

                    const diffInDays = Math.round(
                        (currentDate.getTime() - anchorDate.getTime()) / (1000 * 60 * 60 * 24)
                    );

                    const weekNumber = Math.floor(diffInDays / 7);

                    if (weekNumber < 0 || weekNumber % this.weekInterval !== 0) {
                        current.setDate(current.getDate() + 1);
                        continue;
                    }
                }

                const weekDay = this.selectedWeekdays.find(d => d.code === day);

                if (weekDay) {
                    if (weekDay.morning) {
                        this.selectedWorkDays.push({
                            dayPartId: 1,
                            isSelected: true,
                            year: current.getFullYear(),
                            month: current.getMonth(),
                            day: current.getDate()
                        });
                    }

                    if (weekDay.afternoon) {
                        this.selectedWorkDays.push({
                            dayPartId: 2,
                            isSelected: true,
                            year: current.getFullYear(),
                            month: current.getMonth(),
                            day: current.getDate()
                        });
                    }
                }
            }

            current.setDate(current.getDate() + 1);
        }

        this.compareWithExistingProfileWorkDays();              
    }

    private generateNthWeekInMonths(start: Date, end: Date): void {
        const firstSelectedWeekday = this.selectedWeekdays[0].code;

        const currentMonth = new Date(start.getFullYear(), start.getMonth(), 1);

        // eslint-disable-next-line no-unmodified-loop-condition
        while (currentMonth <= end) {
            const year = currentMonth.getFullYear();
            const month = currentMonth.getMonth();

            const diffInMonths =
                (year - start.getFullYear()) * 12 +
                (month - start.getMonth());

            if (diffInMonths % this.monthInterval !== 0) {
                currentMonth.setMonth(month + 1);
                continue;
            }

            // Trouver le lundi de la première semaine qui touche le mois
            const firstDayOfMonth = new Date(year, month, 1);

            const firstWeekMonday = new Date(firstDayOfMonth);
            const day = firstWeekMonday.getDay() || 7;
            firstWeekMonday.setDate(firstWeekMonday.getDate() - day + 1);

            // Aller à la semaine N
            const targetWeekMonday = new Date(firstWeekMonday);
            targetWeekMonday.setDate(
                firstWeekMonday.getDate() + (this.everyNthWeek - 1) * 7
            );

            // Trouver la première semaine contenant le premier weekday sélectionné
            while (true) {
                const targetDate = new Date(targetWeekMonday);
                targetDate.setDate(
                    targetWeekMonday.getDate() + (firstSelectedWeekday - 1)
                );

                if (
                    targetDate.getMonth() === month &&
                    targetDate >= start &&
                    targetDate <= end
                ) {
                    break;
                }

                // sinon semaine suivante
                targetWeekMonday.setDate(targetWeekMonday.getDate() + 7);

                // sécurité : si on dépasse le mois → stop
                if (targetWeekMonday.getMonth() !== month &&
                    new Date(targetWeekMonday.getFullYear(), targetWeekMonday.getMonth(), 1) > currentMonth) {
                    break;
                }
            }

            // Générer tous les weekdays sélectionnés dans cette semaine
            for (const weekday of this.selectedWeekdays) {
                const targetDate = new Date(targetWeekMonday);
                targetDate.setDate(
                    targetWeekMonday.getDate() + (weekday.code - 1)
                );

                if (
                    targetDate.getMonth() !== month ||
                    targetDate < start ||
                    targetDate > end
                ) continue;

                if (weekday.morning) {
                    this.selectedWorkDays.push({
                        dayPartId: 1,
                        isSelected: true,
                        year: targetDate.getFullYear(),
                        month: targetDate.getMonth(),
                        day: targetDate.getDate()
                    });
                }

                if (weekday.afternoon) {
                    this.selectedWorkDays.push({
                        dayPartId: 2,
                        isSelected: true,
                        year: targetDate.getFullYear(),
                        month: targetDate.getMonth(),
                        day: targetDate.getDate()
                    });
                }
            }

            currentMonth.setMonth(month + 1);
        }
        this.compareWithExistingProfileWorkDays();
    }
}
